/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import AddIcon from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import Check from '@mui/icons-material/Check';
import get from 'lodash/get';
import map from 'lodash/map';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TerminalPlatformsEnum } from '../../../shared/entities/terminalPlatform';
import { abbreviateNetworkName } from '../../../utils/StringUtils';
import Scrollbars from '../layout/Scrollbars';
import { TableOptions } from '../table/types';
import StyledProps from '../theme/AppThemeInterface';

import Button from '@mui/material/Button';
import appTheme from '../theme/appTheme.json';
import { Paper } from '@mui/material';
import { NetworkOrigin } from '../../../shared/entities/terminal';


export interface FilterSelectItem {
  label: string;
  key: string | number | TerminalPlatformsEnum;
  path: any[];
  parentKey?: string | number | TerminalPlatformsEnum;
  parentLabel?: string;
  children?: { [key: string]: FilterSelectItem };
}

export interface FilterSelectProps {
  menu: FilterSelectItem;
  filters: TableOptions['filters'];
  onSelect: (item: FilterSelectItem) => any;
}

export interface FilterSelectState {
  isOpen: boolean;
  path: string[];
}

const FilterSelect = (props: FilterSelectProps) => {
  const { filters, menu, onSelect } = props;
  let container: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [path, setPath] = useState<any[]>([]);

  const memoizedHandleDocumentClickHandler = useCallback(
    (event: MouseEvent) => {
      if (container.current && container.current !== event.target && container.current.contains(event.target as Node)) {
      } else {
        setIsOpen(false);
        setPath([]);
        document.removeEventListener('click', memoizedHandleDocumentClickHandler, true);
      }
    },
    [container],
  );

  useEffect(() => {
    document.removeEventListener('click', memoizedHandleDocumentClickHandler, true);
  }, [memoizedHandleDocumentClickHandler]);

  const addEventHandler = () => document.addEventListener('click', memoizedHandleDocumentClickHandler, true);

  const open = () => {
    setIsOpen(true);
    addEventHandler();
  };
  const handleButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
      setPath([]);
      document.removeEventListener('click', memoizedHandleDocumentClickHandler, true);
    } else {
      open();
    }
  };

  const handleItemClick = (item: FilterSelectItem) => (event: MouseEvent) => {
    setPath([...path, item.path[item.path.length - 1]]);
  };

  const handleFilterClick = (item: FilterSelectItem) => () => {
    setIsOpen(false);

    setPath([]);
    document.removeEventListener('click', memoizedHandleDocumentClickHandler, true);
    onSelect(item);
  };

  const handleBackClick = () => {
    setPath(path.slice(0, -1));
  };

  // render menu without a back button
  const renderTopMenu = () => {
    return (
      <FilterList id="fleet_view_filter_list">
        <FilterListHeader>{menu.label}:</FilterListHeader>
        {map(menu.children, item => (
          <FilterListItem
            key={item.label}
            data-test={`fleet__view__filter__list__item__${item.label}`}
            onClick={handleItemClick(item)}
          >
            {item.label}
          </FilterListItem>
        ))}
      </FilterList>
    );
  };

  const renderMenu = (activeMenu: FilterSelectItem) => {
    // This checks if the browser is Internet Explorer 6-11
    const isIE = document && !!get(document, 'documentMode');

    return (
      <FilterList>
        <FilterListHeader>
          <ArrowBack onClick={handleBackClick} />
          {activeMenu.label}:
        </FilterListHeader>
        <Scrollbars
          style={{ right: '4px' }}
          autoHeight
          autoHeightMin={32}
          autoHeightMax={496}
          hideTracksWhenNotNeeded={false}
        >
          <StyledFilterListContainer id="fleet_view_filter_sublist">
            {activeMenu.children &&
              map(activeMenu.children, item => (
                <FilterListItem
                  key={item.label}
                  onClick={handleFilterClick(item)}
                  data-test={`fleet__view__filter__list__sub-item__${item.label}`}
                >
                  {abbreviateNetworkName(item.label as NetworkOrigin)}
                  {filters.find(f => f[0] === item.parentKey && f[2] === item.key) && (
                    <CheckContainer>
                      <CheckIcon />
                    </CheckContainer>
                  )}
                </FilterListItem>
              ))}
            {
              /* If the browser is IE6-11. add another blank list item since the last item is being cut off*/
              isIE && <DummyFilterListItem />
            }
          </StyledFilterListContainer>
        </Scrollbars>
      </FilterList>
    );
  };

  const isTopMenu = path.length === 0;
  const activeMenu = path.reduce<FilterSelectItem | null>((item, key) => {
    if (!item || !item.children) return null;
    return item.children[key] || null;
  }, menu);

  return (
    <FilterContainer ref={container}>
      <Button
        color="primary"
        disableRipple
        sx={{
          color: appTheme.buttonSelectedColor,
          backgroundColor: '#16232c',
          borderColor: '#141d24',
          padding: '0px 14px 0px 18px',
          display: 'flex',
          alignItems: 'center',
          ':hover': {
            backgroundColor: '#16232c',
          },
        }}
        id="fleet_view_filter_button"
        data-test="fleet__view__filter__button"
        onClick={handleButtonClick}
      >
        ADD FILTER <AddIcon style={{ fontSize: '17px', marginLeft: '6px', color: appTheme.filterButtonIconColor}} />
      </Button>
      <FilterListContainer isOpen={isOpen}>
        {isTopMenu && renderTopMenu()}
        {!isTopMenu && activeMenu && renderMenu(activeMenu)}
      </FilterListContainer>
    </FilterContainer>
  );
};

export default FilterSelect;
const StyledFilterListContainer = styled(Paper)`
  width: 200px;
  min-height: 32px;
  max-height: 500px;
  display: block;
  position: relative;
  box-sizing: border-box;
  padding-bottom: 0px;
`;
const FilterContainer = styled.div`
  margin-left: 1rem;
`;
const FilterListContainer = styled(Paper)`
  display: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? 'block' : 'none')};
  position: absolute;
  width: 228px;
  padding: 8px 0px;
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
`;

const FilterList = styled.div``;

const FilterListHeader = styled.div`
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 18px;
  padding: 8px 16px;

  display: flex;
  align-items: center;

  svg {
    height: 14px;

    :hover {
      cursor: pointer;
    }
  }
`;

const FilterListItem = styled.div`
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 16px;
  line-height: 16px;
  padding: 8px 16px;
  box-sizing: border-box;
  width: 228px;
  display: block;
  align-items: center;
  min-height: 32px;

  &:hover {
    cursor: pointer;
    background-color: #16232c;
  }
`;
// This is a blank filter list item which is added ot the end of the list in Internet Explorer so the last item in the list is not cut off
const DummyFilterListItem = styled.div`
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 16px;
  line-height: 16px;
  padding: 8px 16px;
  cursor: pointer;
  box-sizing: border-box;
  width: 228px;
  display: block;
  align-items: center;
  min-height: 17px;
`;

const CheckContainer = styled.div`
  display: flex;
  margin-left: auto;
`;

const CheckIcon = styled(Check)`
  height: 14px;
  width: 14px;
  margin-bottom: 2px;
  position: relative;

  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  display: inline-block;
`;
